import { ServiceVariationSelectField } from "@/services/backend/omni/servicevariation/components/service-variation-select";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInputField } from "@/shared/components/field/input-field";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  PatchRequest,
  updateOmniPositionsBaseDataCache,
  useOmniPositionsBaseDataPatchMutation,
  useOmniPositionsBaseDataShowQuery,
} from "../api/base-data-api";
import {
  PositionsBaseDataPatch,
  patchPositionsBaseData,
} from "../types/base-data";
import { PositionOptionsTable } from "./position-options-table";
import { PositionOptionAvailabilityItem } from "./position-option-details-dialog";
import { PositionKind } from "../types/position-kind";
import { PricesUsedOf } from "../types/price-used";

/**
 * PositionsBaseDataFieldsConfig configures the {@link PositionsBaseDataFields}
 * per service kind: availabilities are the places its options may be
 * offered at, subPositionKinds the kinds their sub-positions may have,
 * pricesUsed the prices their positions may show.
 */
export type PositionsBaseDataFieldsConfig = {
  availabilities: PositionOptionAvailabilityItem[];
  subPositionKinds: PositionKind[];
  pricesUsed: PricesUsedOf;
};

export function PositionsBaseDataFields({
  id,
  config,
}: {
  id: UUID;
  config: PositionsBaseDataFieldsConfig;
}) {
  const {
    data,
    error: dataError,
    isLoading,
  } = useOmniPositionsBaseDataShowQuery({ id });
  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateOmniPositionsBaseDataCache,
    useOmniPositionsBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }
  if (isLoading || !data) {
    return <CardContentSkeleton />;
  }

  const patch = (p: PositionsBaseDataPatch) =>
    set({ ...request, ...p }, patchPositionsBaseData(data, p));

  return (
    <div className="grid gap-4">
      {/* A rename to a taken label is a conflict, not a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => patch({ label })}
        fieldName="label"
      />
      <ServiceVariationSelectField
        serviceKind={data.serviceKind}
        value={data.serviceVariation}
        onValueChange={(serviceVariation) => patch({ serviceVariation })}
        fieldName="serviceVariation"
        description={t(
          "Optional. Leistungsverzeichnisse dieser Variation bieten diese Positionen an, alle übrigen die als aktiv markierten Basisdaten. Eine Variation ist je Unternehmen eindeutig.",
        )}
      />
      <Field>
        <FieldLabel>{t("Positionen")}</FieldLabel>
        <FieldDescription>
          {t("Werden beim Hinzufügen einer Position angeboten.")}
        </FieldDescription>
        <PositionOptionsTable
          options={data.options}
          onOptionsChange={(options) => patch({ options })}
          fieldName="options"
          companyId={data.companyId}
          serviceKind={data.serviceKind}
          serviceVariation={data.serviceVariation}
          pricesUsed={config.pricesUsed}
          availabilities={config.availabilities}
          subPositionKinds={config.subPositionKinds}
        />
      </Field>
    </div>
  );
}
